import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { GaugeIcon, MapPinIcon, WarningDiamondIcon, DatabaseIcon, ClipboardTextIcon, SignOutIcon, LightningIcon, ClipboardIcon, KeyIcon } from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", icon: GaugeIcon, roles: ["admin", "analyst", "auditor"] },
  { to: "/sites", label: "Sites", icon: MapPinIcon, roles: ["admin", "analyst", "auditor"] },
  { to: "/discrepancies", label: "Discrepancies", icon: WarningDiamondIcon, roles: ["admin", "analyst"] },
  { to: "/review", label: "Pending Review", icon: ClipboardIcon, roles: ["admin"] },
  { to: "/sources", label: "Sources & Ingestion", icon: DatabaseIcon, roles: ["admin", "analyst"] },
  { to: "/audit", label: "Field Audit", icon: ClipboardTextIcon, roles: ["admin", "analyst", "auditor"] },
  { to: "/partners", label: "Partners", icon: KeyIcon, roles: ["admin"] },
];

const ROLE_LABEL = { admin: "Platform Admin", analyst: "Data Ops Analyst", auditor: "Field Auditor" };

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-slate-50 lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="hidden lg:flex flex-col border-r border-slate-200 bg-white">
        <div className="flex items-center gap-2 px-6 h-16 border-b border-slate-100">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-white">
            <LightningIcon weight="fill" size={18} />
          </div>
          <div>
            <div className="font-display font-bold text-slate-900 leading-tight">ChargeSure</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Hyderabad Pilot</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.filter((n) => n.roles.includes(user?.role)).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.label.split(" ")[0].toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }>
              <n.icon size={18} weight="duotone" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-100 p-4">
          <div className="mb-3">
            <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
            <div className="text-xs text-emerald-600">{ROLE_LABEL[user?.role] || user?.role}</div>
          </div>
          <button data-testid="logout-btn" onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-50 hover:text-rose-600">
            <SignOutIcon size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white/90 backdrop-blur px-4 h-14">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-600 text-white"><LightningIcon weight="fill" size={16} /></div>
          <span className="font-display font-bold text-slate-900">ChargeSure</span>
        </div>
        <button data-testid="mobile-logout-btn" onClick={handleLogout} className="text-slate-500"><SignOutIcon size={20} /></button>
      </div>

      <main className="min-w-0">
        <Outlet />
        {/* Mobile bottom nav */}
        <div className="lg:hidden sticky bottom-0 z-20 grid border-t border-slate-200 bg-white" style={{ gridTemplateColumns: `repeat(${NAV.filter((n) => n.roles.includes(user?.role)).length}, minmax(0, 1fr))` }}>
          {NAV.filter((n) => n.roles.includes(user?.role)).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 text-[10px] ${isActive ? "text-emerald-600" : "text-slate-500"}`}>
              <n.icon size={20} weight="duotone" />
              {n.label.split(" ")[0]}
            </NavLink>
          ))}
        </div>
      </main>
    </div>
  );
}
